import { DateFormat, TimeFormat } from '@range.io/basic-types'
import { groupBy, pluck, uniq } from '@range.io/functional'
import {
    Canvas as PdfCanvas,
    Document,
    Font,
    Image,
    Path,
    StyleSheet,
    Svg,
    Text,
    View,
    Link,
} from '@react-pdf/renderer'
import { format } from 'date-fns'
import React, { useRef } from 'react'
import { humanizeByteCount } from '../../helpers'
import { Footer, Header, Page, PageContent, UserAvatar } from './shared'

import InterBold from './fonts/Inter-Bold.ttf'
import InterLight from './fonts/Inter-Light.ttf'
import InterMedium from './fonts/Inter-Medium.ttf'
import InterRegular from './fonts/Inter-Regular.ttf'
import InterSemiBold from './fonts/Inter-SemiBold.ttf'

Font.register({
    family: 'Inter',
    fonts: [
        { fontWeight: 300, src: InterLight },
        { fontWeight: 400, src: InterRegular },
        { fontWeight: 500, src: InterMedium },
        { fontWeight: 600, src: InterSemiBold },
        { fontWeight: 700, src: InterBold },
    ],
})

const styles = StyleSheet.create({
    headerPrimaryLabel: { color: '#333333', fontSize: '12pt', fontWeight: '700' },
    headerSecondaryLabel: { fontSize: '11pt' },
    itemLabel: { color: '#333333', fontSize: '10pt', fontWeight: '600' },
    itemDescription: { color: '#333333', fontSize: '10pt', fontWeight: '500' },
    itemParentName: {
        fontSize: '14pt',
        fontWeight: '600',
        whiteSpace: 'wrap',
    },
    itemIdentifier: {
        color: '#333333',
        fontSize: '10pt',
        fontWeight: '500',
    },
    itemTimestamp: {
        color: '#333333',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontSize: '9pt',
    },
    itemUserName: {
        fontSize: '10pt',
        fontWeight: '500',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    itemTagName: {
        border: '1pt solid #5D81FF',
        color: '#5D81FF',
        fontSize: '9pt',
        fontWeight: '600',
        borderRadius: '75pt',
        padding: '2.5pt 5pt 1.5pt 5pt',
        whiteSpace: 'nowrap',
        display: 'flex',
        ai: 'center',
    },
    commentLabel: {
        color: '#333333',
        fontSize: '9pt',
        fontWeight: '600',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    sheetHeader: {
        display: 'flex',
        flexDirection: 'row',
        gap: '6pt',
        alignItems: 'center',
        width: '100%',
        paddingBottom: '24pt',
    },
    textLabel: {
        fontSize: '10pt',
        fontWeight: '500',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    sheetHeaderLocation: {
        fontSize: '11pt',
        minWidth: 'auto',
        maxWidth: '413pt',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontWeight: '500',
    },
    sheetHeaderSummary: {
        maxWidth: '173pt',
        color: '#333333',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontSize: '9pt',
        paddingBottom: '2pt',
    },
    summary: {
        height: 'auto',
        backgroundColor: '#FAFAFA',
        border: '1pt solid #D9D9D9',
        borderRadius: '5pt',
        padding: '14pt',
        gap: '14pt',
        margin: '18pt 0pt 24pt 0pt',
    },
    summaryContributorItem: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '6pt',
        minWidth: '246pt',
        maxWidth: '246pt',
    },
    summaryContributorName: {
        fontSize: '11pt',
        fontWeight: '500',
        minWidth: 'auto',
        maxWidth: '167pt',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    summaryContributorPhotoCount: {
        maxWidth: '173pt',
        color: '#333333',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontSize: '10pt',
    },
    summaryHeader: {
        fontSize: '10pt',
        fontWeight: '700',
        textTransform: 'uppercase',
    },
    summaryRow: { display: 'flex', flexDirection: 'row', gap: '12pt', pb: '15pt', alignItems: 'start', width: '100%' },
    summaryItemName: {
        fontSize: '9pt',
        fontWeight: '700',
        textTransform: 'uppercase',
        color: '#333333',
    },
    summaryItemValue: {
        fontSize: '12pt',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontWeight: '500',
    },
})

const THUMBNAIL_HEIGHT = '175pt'

const Divider = () => (
    <PdfCanvas
        style={{ width: '100%', height: 'auto', minHeight: '1px', margin: '18pt 0' }}
        paint={(painter, availableWidth) =>
            painter.moveTo(0, 0).lineWidth(1).strokeColor('#D9D9D9').lineTo(availableWidth, 0).stroke()
        }
    />
)

const DividerSummary = () => (
    <PdfCanvas
        style={{ width: '100%', height: 'auto', minHeight: '1px', margin: '4pt 0' }}
        paint={(painter, availableWidth) =>
            painter.moveTo(0, 0).lineWidth(1).strokeColor('#D9D9D9').lineTo(availableWidth, 0).stroke()
        }
    />
)

const SummaryHeaderItem = ({ name, value }) => (
    <View style={{ display: 'flex', flexDirection: 'column', gap: '9pt' }}>
        <Text style={styles.summaryItemName}>{name}</Text>
        <Text style={styles.summaryItemValue}>{value}</Text>
    </View>
)

const LayersIcon = () => (
    <Svg viewBox="0 0 16 16" width="12" height="12">
        <Path
            d="M16 8.01283C15.9998 7.9563 15.985 7.90078 15.9569 7.8517C15.9289 7.80261 15.8886 7.76165 15.84 7.73281L13.8397 6.50603L15.84 5.28592C15.8882 5.25519 15.9278 5.21281 15.9553 5.1627C15.9828 5.1126 15.9972 5.05637 15.9972 4.99922C15.9972 4.94208 15.9828 4.88585 15.9553 4.83574C15.9278 4.78564 15.8882 4.74326 15.84 4.71253L8.17222 0.0454414C8.1193 0.0156504 8.0596 0 7.99887 0C7.93814 0 7.87843 0.0156504 7.82551 0.0454414L0.157761 4.71253C0.109576 4.74326 0.0699133 4.78564 0.0424368 4.83574C0.0149603 4.88585 0.000556639 4.94208 0.000556639 4.99922C0.000556639 5.05637 0.0149603 5.1126 0.0424368 5.1627C0.0699133 5.21281 0.109576 5.25519 0.157761 5.28592L2.15804 6.50603L0.157761 7.73281C0.109518 7.7627 0.0697064 7.80442 0.0420984 7.85401C0.0144904 7.9036 0 7.95941 0 8.01617C0 8.07292 0.0144904 8.12873 0.0420984 8.17832C0.0697064 8.22791 0.109518 8.26963 0.157761 8.29952L2.15804 9.5063L0.157761 10.7131C0.109576 10.7438 0.0699133 10.7862 0.0424368 10.8363C0.0149603 10.8864 0.000556639 10.9426 0.000556639 10.9998C0.000556639 11.0569 0.0149603 11.1131 0.0424368 11.1632C0.0699133 11.2134 0.109576 11.2557 0.157761 11.2865L7.82551 15.9536C7.87822 15.984 7.93801 16 7.99887 16C8.05973 16 8.11952 15.984 8.17222 15.9536L15.84 11.2865C15.8882 11.2557 15.9278 11.2134 15.9553 11.1632C15.9828 11.1131 15.9972 11.0569 15.9972 10.9998C15.9972 10.9426 15.9828 10.8864 15.9553 10.8363C15.9278 10.7862 15.8882 10.7438 15.84 10.7131L13.8397 9.5063L15.84 8.29952C15.8891 8.26961 15.9297 8.22748 15.9577 8.17724C15.9858 8.127 16.0003 8.07036 16 8.01283V8.01283ZM7.99887 0.738838L15.0265 5.01256L7.99887 9.28628L0.971209 5.01256L7.99887 0.738838ZM15.0265 11.0131L7.99887 15.2868L0.971209 11.0131L2.74479 9.93301L7.79217 13.0066C7.84488 13.037 7.90467 13.0531 7.96553 13.0531C8.02639 13.0531 8.08618 13.037 8.13889 13.0066L13.1996 9.92634L15.0265 11.0131ZM7.99887 12.3066L0.971209 8.01283L2.79813 6.90606L7.82551 9.96634C7.87822 9.99677 7.93801 10.0128 7.99887 10.0128C8.05973 10.0128 8.11952 9.99677 8.17222 9.96634L13.2129 6.8994L15.0265 8.01283L7.99887 12.3066Z"
            fill="#333333"
        />
    </Svg>
)

const SheetHeader = ({ locationCount, locationLabel, photoCount }) => {
    const sheetSummaryLabel = `(${formatPhotoCountLabel(photoCount)} across ${formatLocationCountLabel(locationCount)})`
    return (
        <View style={styles.sheetHeader}>
            <LayersIcon />
            <View style={{ display: 'flex', flexDirection: 'row', gap: '3pt', alignItems: 'center' }}>
                <Text style={styles.sheetHeaderLocation}>{locationLabel}</Text>
                <Text style={styles.summaryContributorPhotoCount}>{sheetSummaryLabel}</Text>
            </View>
        </View>
    )
}

const CollaborationIdentifier = ({ projectIdentifier, collaborationIdentifier }) => {
    if (!collaborationIdentifier) return null
    return (
        <Text style={styles.itemIdentifier}>
            ID: {projectIdentifier}-{collaborationIdentifier}
        </Text>
    )
}

const LocationSnapshot = ({ locationSnapshotUrl, css }) => (
    <View
        style={{
            borderRadius: '5pt',
            border: '1pt solid #DDDDDD',
            ...css,
        }}
    >
        <Link src={locationSnapshotUrl}>
            <Image
                style={{
                    'object-fit': 'contain',
                    width: 'auto',
                    height: 'auto',
                    borderRadius: '4pt',
                }}
                src={locationSnapshotUrl}
            />
        </Link>
    </View>
)

const NonImageUploadFill = ({ fileName, fileSize, fileType }) => {
    const text = () => {
        if (fileType === 'pdf') return '.pdf'
        if (fileType === 'xlsx') return '.xlxs'
        if (fileType === 'docx') return '.docx'
        return 'WHOA!'
    }

    const columnCss = {
        margin: 'auto',
        padding: '0 5pt',
        width: 'auto',
        minWidth: '68pt',
        maxWidth: '90%',
        height: '83pt',
        borderRadius: '8pt',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        justifyContent: 'center',
        border: '1pt solid #dddddd',
    }

    const humanized = humanizeByteCount(fileSize)
    return (
        <View
            style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#fafafa',
                border: '1pt solid #dddddd',
                borderRadius: '6pt',
                height: THUMBNAIL_HEIGHT,
            }}
        >
            <View style={columnCss}>
                <Text style={{ fontSize: '15pt', fontWeight: 700, height: '23pt', color: '#333333' }}>{text()}</Text>
                {humanized && (
                    <Text
                        style={{
                            fontSize: '11pt',
                            fontWeight: 500,
                            color: '#333333',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {humanized}
                    </Text>
                )}
            </View>
            <View
                style={{
                    width: '100%',
                    height: 30,
                    padding: '4pt 12pt 4pt 8pt',
                    borderTop: '1pt solid #dddddd',
                    backgroundColor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'row',
                    overflow: 'hidden',
                }}
            >
                <Text
                    style={{
                        display: 'flex',
                        fontSize: '11pt',
                        fontWeight: 500,
                        color: '#333333',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {fileName.split('')}
                </Text>
            </View>
        </View>
    )
}

const CommentsList = ({ comments }) => {
    const formatDate = date => format(Date.parse(date), 'd MMM, yyyy @ h:mm' + 'aaa')

    return comments.map(comment => (
        <View key={comment.id} style={{ flexDirection: 'column', gap: '6pt' }} wrap={false}>
            <View
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '4pt',
                }}
            >
                <UserAvatar name={comment.createdBy.fullName} url={comment.createdBy.avatarUrl} size={12} />
                <Text style={styles.commentLabel}>{comment.createdBy.fullName}</Text>
                <Text style={styles.commentLabel}>•</Text>
                <Text style={styles.commentLabel}>{formatDate(comment.createdAt)}</Text>
            </View>
            <Text style={styles.itemDescription}>{comment.text}</Text>
        </View>
    ))
}

const UploadThumbnail = ({ fileSize, fileType, imageUrl, name, thumbnailUrl }) => {
    const isImage = fileType === 'image' || fileType === 'jpeg' || fileType === 'png'

    if (isImage && imageUrl)
        return (
            <View
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'start',
                    height: THUMBNAIL_HEIGHT,
                }}
            >
                <Link src={imageUrl}>
                    <Image
                        style={{
                            objectFit: 'cover',
                            borderRadius: 5,
                            border: '1pt solid #D9D9D9',
                            height: THUMBNAIL_HEIGHT,
                        }}
                        src={thumbnailUrl}
                    />
                </Link>
            </View>
        )

    return <NonImageUploadFill fileName={name} fileSize={fileSize} fileType={fileType} />
}

const ReportItem = ({
    index,
    comments,
    date,
    description,
    fileSize,
    fileType,
    imageUrl,
    thumbnailUrl,
    locationLabel,
    locationSnapshotUrl,
    name,
    parentName,
    tagNames,
    userName,
    key,
    projectIdentifier,
    collaborationIdentifier,
    userAvatarUrl,
}) => {
    return (
        <View
            style={{
                display: 'flex',
                flexDirection: 'column',
                marginBottom: '0pt',
            }}
        >
            <View
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6pt',
                    marginBottom: '10pt',
                    width: '100%',
                }}
            >
                <View
                    style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '10pt',
                        marginBottom: '4pt',
                        flex: '1 0 auto',
                    }}
                >
                    <Text style={styles.itemParentName}>
                        {index}. {parentName || 'Untitled photo pin'}
                    </Text>
                    <CollaborationIdentifier
                        projectIdentifier={projectIdentifier}
                        collaborationIdentifier={collaborationIdentifier}
                    />
                </View>
                <View
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        gap: '6pt',
                    }}
                    wrap={false}
                >
                    {tagNames.map((tagName, idx) => (
                        <Text style={styles.itemTagName} key={`tag_name_${key}_${idx}`}>
                            {tagName}
                        </Text>
                    ))}
                </View>
            </View>
            <View
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    gap: '16pt',
                    width: '100%',
                }}
                wrap={false}
            >
                <View
                    wrap={false}
                    style={{
                        flex: '0 0 50%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'start',
                    }}
                >
                    <UploadThumbnail
                        fileSize={fileSize}
                        fileType={fileType}
                        imageUrl={imageUrl}
                        thumbnailUrl={thumbnailUrl}
                        name={name}
                    />
                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: '6pt',
                            marginTop: '8pt',
                        }}
                    >
                        <UserAvatar name={userName} url={userAvatarUrl} size={12} />
                        <Text style={styles.itemUserName}>{userName}</Text>
                        <Text style={styles.itemTimestamp}>{date}</Text>
                    </View>
                </View>
                <View
                    wrap={false}
                    style={{ flex: '0 0 50%', display: 'flex', flexDirection: 'column', justifyContent: 'start' }}
                >
                    <LocationSnapshot
                        locationSnapshotUrl={locationSnapshotUrl}
                        css={{
                            height: THUMBNAIL_HEIGHT,
                        }}
                    />
                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: '6pt',
                            marginTop: '8pt',
                        }}
                    >
                        <LayersIcon />
                        <Text style={styles.textLabel}>{locationLabel}</Text>
                    </View>
                </View>
            </View>
            {description && (
                <View
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                    }}
                >
                    <Divider />
                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'left',
                            gap: '4pt',
                            width: '100%',
                        }}
                    >
                        <View style={{ flex: '0 0 15%' }}>
                            <Text style={styles.itemLabel}>Description:</Text>
                        </View>
                        <View style={{ flex: '1 1 83%', flexWrap: 'wrap' }}>
                            <Text style={styles.itemDescription}>{description}</Text>
                        </View>
                    </View>
                </View>
            )}
            {comments?.length && (
                <View
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                    }}
                >
                    <Divider />
                    <View
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'left',
                            gap: '4pt',
                            width: '100%',
                        }}
                    >
                        <View style={{ flex: '0 0 15%' }}>
                            <Text style={styles.itemLabel}>Comments:</Text>
                        </View>
                        <View
                            style={{
                                flex: '1 1 83%',
                                flexWrap: 'wrap',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'left',
                                gap: '16pt',
                            }}
                        >
                            <CommentsList comments={comments} />
                        </View>
                    </View>
                </View>
            )}
        </View>
    )
}

const formatPhotoCountLabel = photoCount => (photoCount === 1 ? '1 photo' : `${photoCount} photos`)

const formatLocationCountLabel = locationCount => (locationCount === 1 ? '1 location' : `${locationCount} locations`)

const MediaReportDoc = ({ contributors, organizationName, logo, projectName, tagNamesUsed, uploadData }) => {
    if (!Array.isArray(uploadData)) return null
    const itemsCount = useRef(0)

    const groupedByCanvas = groupBy(u => u.canvas, uploadData)
    const groupedByCollaboration = groupBy(u => u.parentId, uploadData)

    const parseDate = date => {
        if (typeof date === 'string') return DateFormat.parseISOStringToDate(date)
        return date
    }
    const datesOfUploads = pluck('createdAt', uploadData).sort(
        (a, b) => parseDate(a).getTime() - parseDate(b).getTime()
    )
    const dateRangeLabel = [datesOfUploads[0], datesOfUploads.at(-1)]
        .map(date => DateFormat.primary(parseDate(date)))
        .join(' - ')

    const totalPhotos = Object.keys(uploadData).length
    const totalLocations = Object.keys(groupedByCollaboration).length
    const totalSheets = Object.keys(groupedByCanvas).length

    return (
        <Document>
            <Page>
                <PageContent>
                    <Header logo={logo} organizationName={organizationName} projectName={projectName} />
                    <View style={styles.summary}>
                        <Text style={styles.summaryHeader}>Report Summary</Text>
                        <View style={styles.summaryRow}>
                            <View
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minWidth: '248pt',
                                    maxWidth: '248pt',
                                    gap: '9pt',
                                }}
                            >
                                <Text style={styles.summaryItemName}>Photos taken on</Text>
                                <Text style={styles.summaryItemValue}>{dateRangeLabel}</Text>
                            </View>
                            <SummaryHeaderItem name="Total photos" value={totalPhotos} />
                            <SummaryHeaderItem name="Total locations" value={totalLocations} />
                            <SummaryHeaderItem name="Total sheets" value={totalSheets} />
                        </View>
                        <DividerSummary />
                        <View style={styles.summaryRow}>
                            <View style={{ display: 'flex', gap: '9pt', maxWidth: '246pt', minWidth: '246pt' }}>
                                <Text style={styles.summaryItemName}>Contributors</Text>
                                <View style={{ gap: '9pt' }}>
                                    {contributors.map((contributor, idx) => (
                                        <View style={styles.summaryContributorItem} key={`contributor_report_${idx}`}>
                                            <UserAvatar name={contributor.name} url={contributor.avatarUrl} size={12} />
                                            <Text style={styles.summaryContributorName}>{contributor.name}</Text>
                                            <Text style={styles.summaryContributorPhotoCount}>
                                                ({formatPhotoCountLabel(contributor.photoCount)})
                                            </Text>
                                        </View>
                                    ))}
                                </View>
                            </View>
                            <View style={{ display: 'flex', gap: '9pt' }}>
                                <Text style={styles.summaryItemName}>Photos contain the following tags</Text>
                                <View
                                    style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '6pt' }}
                                    wrap={false}
                                >
                                    {tagNamesUsed.map((tagName, idx) => (
                                        <Text style={styles.itemTagName} key={`tagName_report_${idx}`}>
                                            {tagName}
                                        </Text>
                                    ))}
                                </View>
                            </View>
                        </View>
                    </View>
                    {Object.keys(groupedByCanvas).map((canvasId, idx) => (
                        <View key={`report_group_${idx}`}>
                            <SheetHeader
                                locationCount={uniq(pluck('parent', groupedByCanvas[canvasId])).length}
                                locationLabel={groupedByCanvas[canvasId][0].locationLabel}
                                photoCount={groupedByCanvas[canvasId].length}
                            />
                            {groupedByCanvas[canvasId].map((item, idx) => {
                                const date = TimeFormat.simple(parseDate(item.createdAt), null, false)
                                return (
                                    <View key={`report_item_${idx}`} wrap>
                                        <ReportItem {...item} index={++itemsCount.current} date={date} />
                                        <Divider />
                                    </View>
                                )
                            })}
                        </View>
                    ))}
                </PageContent>
                <Footer organizationName={organizationName} />
            </Page>
        </Document>
    )
}

export default MediaReportDoc
